import { defineStore } from 'pinia'
import {
  MitoGaugeStatus,
  MitoStakingReward,
  MitoStakingActivity
} from '@injectivelabs/sdk-ts'
import { mitoApi } from '@shared/Service'
import {
  streamStakingReward,
  streamStakingActivities
} from '@/store/staking/stream'
import {
  stake,
  unstake,
  claimStake,
  claimRewards,
  claimAllRewards
} from '@/store/staking/message'
import { allocateGauge } from '@/store/staking/admin'
import { STAKING_CONTRACT_ADDRESS } from '@/app/utils/constant'
import { UiStakingPool } from '@/types'

type StakingStoreState = {
  totalActivities: Number
  stakingPools: UiStakingPool[]
  stakingRewards: MitoStakingReward[]
  stakingActivities: MitoStakingActivity[]
}

const initialStateFactory = (): StakingStoreState => ({
  stakingPools: [],
  totalActivities: 0,
  stakingRewards: [],
  stakingActivities: []
})

export const useStakingStore = defineStore('staking', {
  state: (): StakingStoreState => initialStateFactory(),
  actions: {
    stake,
    unstake,
    claimStake,
    claimRewards,
    allocateGauge,
    claimAllRewards,
    streamStakingReward,
    streamStakingActivities,

    async fetchStakingPools() {
      const stakingStore = useStakingStore()

      const { pools } = await mitoApi.fetchStakingPools({
        stakingContractAddress: STAKING_CONTRACT_ADDRESS
      })

      const stakingPools = pools.map((pool) => ({
        ...pool,
        activeGauges: pool.gauges.filter(
          (gauge) => gauge.status === MitoGaugeStatus.Live
        )
      })) as UiStakingPool[]

      stakingStore.$patch({
        stakingPools
      })
    },

    async fetchStakingRewardsByAccount() {
      const stakingStore = useStakingStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.injectiveAddress
      ) {
        return
      }

      const { rewards } = await mitoApi.fetchStakingRewardsByAccount({
        staker: sharedWalletStore.injectiveAddress,
        stakingContractAddress: STAKING_CONTRACT_ADDRESS
      })

      stakingStore.$patch({
        stakingRewards: rewards
      })
    },

    async fetchStakingHistory({
      staker,
      toNumber,
      limit
    }: {
      staker?: string
      toNumber?: number
      limit: number
    }) {
      const stakingStore = useStakingStore()

      const { activities, pagination } = await mitoApi.fetchStakingHistory({
        limit,
        staker,
        toNumber
      })

      if (!toNumber) {
        stakingStore.$patch({
          totalActivities: pagination?.total || 0
        })
      }

      stakingStore.$patch({
        stakingActivities: activities
      })
    },

    reset() {
      const stakingStore = useStakingStore()

      stakingStore.$patch({
        totalActivities: 0,
        stakingRewards: [],
        stakingActivities: []
      })
    }
  }
})
